import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import type { MouseEvent, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { SidebarNavItem } from '@/components/layout/app-shell/navigation-types'
import { UserAvatar } from '@/components/layout/app-shell/user-avatar'
import {
  getDisplayName,
  getUserInitial,
  type AppShellUser,
} from '@/components/layout/app-shell/user-display'
import { resolveBrandAssetSrc } from '@/features/shared/brand/branding-core'
import { useBranding, useShellBrandName } from '@/features/shared/brand/branding-hooks'
import {
  SIDEBAR_CONVERSATION_LIMIT,
  conversationLabel,
} from '@/features/shared/chat/conversation/conversation-utils'
import { PinFilledIcon } from '@/features/shared/chat/conversation/pin-icons'
import { readSearchString } from '@/features/shared/chat/utils'
import { buildChatHistoryPath } from '@/features/user/chat/chat-navigation'
import { listCoreAgentConversations } from '@/features/user/core-agent/api'
import { HistoryIcon } from '@/features/user/shell/navigation-icons'

function ChevronRightIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="m6 4 4 4-4 4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  )
}

function ExternalLinkIcon() {
  return (
    <svg aria-hidden className="size-3.5" fill="none" viewBox="0 0 14 14">
      <path
        d="M5 3h6v6M11 3 4 10"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.25"
      />
    </svg>
  )
}

function DrawerBrand() {
  const brandName = useShellBrandName('user')
  const {
    settings: { logo },
  } = useBranding()

  return (
    <div className="flex h-[60px] shrink-0 items-center gap-3 px-4">
      <span className="flex size-11 items-center justify-center">
        <img alt="" className="size-[22px]" src={resolveBrandAssetSrc(logo)} />
      </span>
      <span className="min-w-0 truncate text-[17px] font-bold text-[var(--color-sidebar-active-text)]">
        {brandName}
      </span>
    </div>
  )
}

function DrawerGroupLabel({ label }: { label: string }) {
  return (
    <div className="px-3 pt-5 pb-1.5 text-xs leading-4 font-semibold text-[var(--color-text-muted)]">
      {label}
    </div>
  )
}

/**
 * 移动端抽屉内容,按设计稿结构渲染:
 * 品牌区 → Hub Agent / 应用广场 / 历史会话 → 我的应用 → 最近会话 → 用户行(进个人中心)。
 * 最近会话直接读 core-agent 会话列表缓存,不依赖页面提供的 navigation children。
 */
export function UserMobileDrawer({
  activePath,
  navigation,
  onNavigate,
  user,
}: {
  activePath?: string
  navigation: SidebarNavItem[]
  onNavigate: () => void
  user: AppShellUser
}) {
  const { i18n, t } = useTranslation('shell')
  const navigate = useNavigate()
  const location = useLocation({ select: (nextLocation) => nextLocation })
  const currentAppId = readSearchString((location.search as { appId?: unknown }).appId) ?? ''
  const hubAgentItem = navigation.find((item) => item.key === 'core-agent')
  const appMarketItem = navigation.find((item) => item.key === 'app-market')
  const businessNavigationItem = navigation.find((item) => item.key === 'business-navigation')
  const installedApps =
    navigation.find((item) => item.key === 'quick-apps-heading')?.mobileChildren ??
    navigation.filter((item) => item.variant === 'app')
  const fallbackName = t('userMenu.fallbackName')
  // 历史会话保留当前应用上下文:App Runtime 会话中打开抽屉时进入该应用的历史,
  // 否则回到 Hub Agent 历史(对齐设计稿 history-ready/history-hub 场景)。
  const historyPath = currentAppId ? buildChatHistoryPath(currentAppId) : '/chat?view=history'
  const isHistoryView = readSearchString((location.search as { view?: unknown }).view) === 'history'
  // 高亮规则对齐桌面侧栏的 query 语义:应用按 appId 匹配(会话/历史态都算),
  // Hub Agent 覆盖无 appId 的 chat 会话,历史入口覆盖 history 视图。
  const hubProminent = location.pathname === '/chat' && !currentAppId && !isHistoryView
  const appItemProminent = (path?: string) => {
    if (!currentAppId || !path) return false
    return new URLSearchParams(path.split('?')[1] ?? '').get('appId') === currentAppId
  }

  const recentConversationsQuery = useQuery({
    enabled: Boolean(hubAgentItem),
    gcTime: 10 * 60 * 1000,
    queryFn: () => listCoreAgentConversations(),
    queryKey: ['core-agent-conversations'],
    staleTime: 30_000,
  })
  const recentConversations = (recentConversationsQuery.data?.data ?? []).slice(
    0,
    SIDEBAR_CONVERSATION_LIMIT,
  )

  const goto = (event: MouseEvent<HTMLAnchorElement>, path: string) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      return
    }

    event.preventDefault()
    onNavigate()
    const [pathname, query = ''] = path.split('?')
    void navigate({
      search: Object.fromEntries(new URLSearchParams(query)) as never,
      to: pathname as never,
    })
  }

  const navRow = (
    item: { external?: boolean; icon?: ReactNode; label: ReactNode; path: string },
    options?: { prominent?: boolean },
  ) => (
    <a
      className={`flex min-h-11 items-center gap-3 rounded-[10px] px-3 text-[15px] leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus)] ${
        options?.prominent
          ? 'bg-white font-semibold text-[var(--color-sidebar-active-text)] shadow-[0_1px_2px_rgba(16,24,40,0.06)]'
          : 'text-[var(--color-sidebar-text)]'
      }`}
      href={item.path}
      onClick={(event) => {
        if (item.external) {
          onNavigate()
          return
        }
        goto(event, item.path)
      }}
      rel={item.external ? 'noopener noreferrer' : undefined}
      target={item.external ? '_blank' : undefined}
    >
      {item.icon ? (
        <span className="flex size-5 shrink-0 items-center justify-center">{item.icon}</span>
      ) : null}
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.external ? (
        <span className="ml-auto text-[var(--color-sidebar-text-muted)]">
          <ExternalLinkIcon />
        </span>
      ) : null}
    </a>
  )

  return (
    <div className="flex h-full flex-col">
      <DrawerBrand />
      <nav className="grid gap-1 px-3">
        {hubAgentItem
          ? navRow(
              {
                icon: hubAgentItem.icon,
                label: hubAgentItem.label,
                path: hubAgentItem.path ?? '/chat',
              },
              { prominent: hubProminent },
            )
          : null}
        {appMarketItem
          ? navRow(
              {
                icon: appMarketItem.icon,
                label: i18n.t('userApps:tabs.market'),
                path: appMarketItem.path ?? '/apps',
              },
              { prominent: activePath === '/apps' },
            )
          : null}
        {businessNavigationItem
          ? navRow({
              external: true,
              icon: businessNavigationItem.icon,
              label: businessNavigationItem.label,
              path: businessNavigationItem.path ?? '#',
            })
          : null}
        {navRow(
          {
            icon: <HistoryIcon />,
            label: t('userNavigation.history'),
            path: historyPath,
          },
          { prominent: isHistoryView },
        )}
      </nav>

      <div aria-hidden className="mx-3 mt-2 border-t border-[var(--color-sidebar-border-subtle)]" />

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {installedApps.length > 0 ? (
          <>
            <DrawerGroupLabel label={i18n.t('userApps:tabs.mine')} />
            <div className="grid gap-1">
              {installedApps.map((item) => (
                <span key={item.key}>
                  {navRow(
                    { icon: item.icon, label: item.label, path: item.path ?? '/apps' },
                    { prominent: appItemProminent(item.path) },
                  )}
                </span>
              ))}
            </div>
          </>
        ) : null}

        {recentConversations.length > 0 ? (
          <>
            <DrawerGroupLabel label={t('userNavigation.recentConversations')} />
            <div className="grid gap-1">
              {recentConversations.map((conversation) => {
                const path = `/chat?conversationId=${encodeURIComponent(conversation.id)}`
                const active = hubProminent && activePath === path
                return (
                  <a
                    className={`flex min-h-10 items-center gap-2 rounded-[10px] px-3 text-[14px] leading-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus)] ${
                      active
                        ? 'bg-white font-medium text-[var(--color-sidebar-active-text)] shadow-[0_1px_2px_rgba(16,24,40,0.06)]'
                        : 'text-[var(--color-sidebar-text)]'
                    }`}
                    href={path}
                    key={conversation.id}
                    onClick={(event) => goto(event, path)}
                  >
                    {conversation.pinned ? (
                      <PinFilledIcon className="size-3.5 shrink-0 text-[var(--color-text-muted)]" />
                    ) : null}
                    <span className="min-w-0 truncate">
                      {conversationLabel(conversation, i18n.t('chat:sidebar.untitled'))}
                    </span>
                  </a>
                )
              })}
            </div>
          </>
        ) : null}
      </div>

      <a
        className="flex h-[68px] shrink-0 items-center gap-3 border-t border-[var(--color-sidebar-border-subtle)] px-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-[var(--color-focus)]"
        href="/profile"
        onClick={(event) => goto(event, '/profile')}
      >
        <span className="flex size-10 shrink-0 items-center justify-center">
          <UserAvatar initial={getUserInitial(user, fallbackName)} src={user.avatar?.trim()} />
        </span>
        <span className="grid min-w-0 flex-1">
          <span className="truncate text-sm font-medium text-[var(--color-sidebar-active-text)]">
            {getDisplayName(user, fallbackName)}
          </span>
          {user.email ? (
            <span className="truncate text-xs text-[var(--color-text-muted)]">{user.email}</span>
          ) : null}
        </span>
        <span className="text-[var(--color-text-muted)]">
          <ChevronRightIcon />
        </span>
      </a>
    </div>
  )
}
